import * as React from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { DropDownList, MultiSelect } from "@progress/kendo-react-dropdowns";
import { Checkbox } from "@progress/kendo-react-inputs";
import { filterBy } from "@progress/kendo-data-query";

import ErrorBanner from "../../components/ErrorBanner";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import {
  useQueryGetPrismInternalUsers,
  useMutatePostSalesAdminMapping,
  useMutatePostSalesAdminMappingRemove,
  useMutatePostSalesAdminMappingRemoveAll,
} from "../../data/services/salesAdminMappingService";
import { normalizeUserId } from "../../utils/salesAdminUserIds";
import PrismUserLookup from "./PrismUserLookup";

// Outcomes worth a per-row explanation. Added/Reactivated/AlreadyMapped are
// self-explanatory from the count alone; these two are not - a bare count
// would leave the admin unable to act.
const OUTCOMES_WITH_DETAIL = ["MappedElsewhere", "Invalid"];

/**
 * Add/edit modal for one Sales Admin's BDM mapping.
 *
 * The underlying table stores AllowAllClients per row, but the server rule is
 * "any active row wins" - so this dialog exposes exactly one checkbox for the
 * whole set rather than one per BDM. That single toggle is what keeps the
 * stored rows from contradicting what the admin saw here: checking it and
 * saving with no BDMs picked is a fully supported path (the server creates a
 * single mapping row with a null BDM), not an error state.
 */
const SalesAdminMappingModal = ({
  salesAdminUserId,
  currentMappings,
  onClose,
  onSubmit,
}) => {
  const isEditing = !!salesAdminUserId;

  const [selectedSalesAdmin, setSelectedSalesAdmin] = React.useState(null);
  const [selectedBdms, setSelectedBdms] = React.useState([]);
  const [allowAllClients, setAllowAllClients] = React.useState(false);
  // Snapshot of the BDM ids the modal preloaded (active rows only), taken
  // once when opening on an existing Sales Admin. SaveSalesAdminMappings is
  // additive-only, so this is what lets Save tell "the admin deselected this
  // BDM" apart from "this BDM was never mapped" - the diff against
  // selectedBdms below drives the explicit remove calls a plain save cannot
  // express. Stays empty when adding a new Sales Admin, so there is nothing
  // to remove on that path.
  const [preloadedBdmIds, setPreloadedBdmIds] = React.useState([]);
  const [salesAdminDataSource, setSalesAdminDataSource] = React.useState([]);
  const [bdmDataSource, setBdmDataSource] = React.useState([]);
  // Users resolved by id this session. They are cached server-side too, but the
  // users query has not refetched yet, so hold them locally to keep them
  // selectable without waiting for a round trip.
  const [extraUsers, setExtraUsers] = React.useState([]);
  const [submitting, setSubmitting] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [showRemoveAllConfirm, setShowRemoveAllConfirm] = React.useState(false);
  const [removingAll, setRemovingAll] = React.useState(false);

  const { data: usersResponse } = useQueryGetPrismInternalUsers();
  const saveHandler = useMutatePostSalesAdminMapping();
  const removeHandler = useMutatePostSalesAdminMappingRemove();
  const removeAllHandler = useMutatePostSalesAdminMappingRemoveAll();

  // What the server has synced. The preload effect below depends on this, not
  // on the combined list, so resolving a user by id cannot retrigger it.
  const fetchedUsers = React.useMemo(
    () => usersResponse?.data ?? [],
    [usersResponse]
  );

  // What the pickers offer: the synced list plus anyone resolved by id this
  // session, so they are selectable without waiting for a refetch.
  const users = React.useMemo(() => {
    const fetchedIds = new Set(
      fetchedUsers.map((u) => normalizeUserId(u.userId))
    );

    return [
      ...fetchedUsers,
      ...extraUsers.filter((u) => !fetchedIds.has(normalizeUserId(u.userId))),
    ];
  }, [fetchedUsers, extraUsers]);

  React.useEffect(() => {
    setSalesAdminDataSource(users.slice());
    setBdmDataSource(users.slice());
  }, [users]);

  // Opening on an existing Sales Admin edits the whole set - their current
  // BDMs and the allow-all flag - rather than only appending to it.
  // A removed (inactive) row must never influence what preloads or what
  // counts as "currently mapped" - the server rule is "any ACTIVE row wins",
  // and this is the one predicate all three derivations below share so they
  // cannot drift apart.
  const activeMappings = (currentMappings ?? []).filter(
    (mapping) => mapping.active !== false
  );

  // Preload from the FETCHED list, deliberately not from `users`.
  //
  // `users` also contains anyone the admin just resolved by id this session.
  // Depending on it here would re-run this effect the moment one is added,
  // resetting selectedBdms to the stored set and silently discarding that
  // addition - and on Save the removal diff would then see nothing added and
  // everything still selected, so the discard would leave no trace. Preload
  // only ever needs to resolve ids that are already stored, and those are all
  // in the fetched list by definition.
  React.useEffect(() => {
    if (!isEditing || fetchedUsers.length === 0) {
      return;
    }

    // Normalized on both sides for the same reason as the BDM ids below:
    // the stored SalesAdminUserId is lowercased while PrismUserDetail.UserId
    // comes back raw-cased, so a case-sensitive compare here misses the user
    // and falls through to the id-as-name placeholder.
    const normalizedSalesAdminUserId = normalizeUserId(salesAdminUserId);
    const admin = fetchedUsers.find(
      (u) => normalizeUserId(u.userId) === normalizedSalesAdminUserId
    );
    setSelectedSalesAdmin(
      admin ?? { userId: salesAdminUserId, userName: salesAdminUserId }
    );

    // SalesAdminMapping rows always store BdmUserId lowercased, but
    // GetPrismInternalUsers returns PrismUserDetail.UserId raw-cased. Compare
    // through the shared normalizer on both sides here (and in the removal
    // diff in handleSubmit below) - a case-sensitive comparison would leave
    // the picker empty and, worse, make the diff think every previously
    // mapped BDM was deselected.
    const bdmIdSet = new Set(
      activeMappings
        .filter((mapping) => mapping.bdmUserId)
        .map((mapping) => normalizeUserId(mapping.bdmUserId))
    );
    setSelectedBdms(
      fetchedUsers.filter((u) => bdmIdSet.has(normalizeUserId(u.userId)))
    );
    setPreloadedBdmIds(Array.from(bdmIdSet));

    setAllowAllClients(
      activeMappings.some((mapping) => mapping.allowAllClients)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, fetchedUsers, salesAdminUserId, currentMappings]);

  const activeMappingCount = activeMappings.length;

  // A user resolved by id is not in the fetched list yet, so hold them in
  // extraUsers (which feeds the pickers) as well as selecting them. The server
  // caches them too, so after the next fetch they are there for everyone.
  const handleSalesAdminResolved = (user) => {
    if (!user) return;

    setExtraUsers((current) =>
      current.some((u) => normalizeUserId(u.userId) === normalizeUserId(user.userId))
        ? current
        : [...current, user]
    );
    setSelectedSalesAdmin(user);
  };

  const handleBdmResolved = (user) => {
    if (!user) return;

    setExtraUsers((current) =>
      current.some((u) => normalizeUserId(u.userId) === normalizeUserId(user.userId))
        ? current
        : [...current, user]
    );
    // Append rather than replace: the admin is adding to a set they may have
    // already curated, and dropping the rest would unmap them on Save.
    setSelectedBdms((current) =>
      current.some((u) => normalizeUserId(u.userId) === normalizeUserId(user.userId))
        ? current
        : [...current, user]
    );
  };

  const canSave =
    !submitting &&
    !!selectedSalesAdmin &&
    (allowAllClients || selectedBdms.length > 0);

  const handleSubmit = async () => {
    setSubmitting(true);

    // Deselecting a preloaded BDM does not survive a plain save - the save
    // command is additive-only - so anything preloaded but no longer
    // selected has to go through the single-remove hook explicitly. Only
    // BDMs still selected go through the additive save below.
    const selectedBdmIds = selectedBdms.map((u) => u.userId);
    // preloadedBdmIds is already normalized (see above); normalize the
    // selected side too, or a case-mismatched id (e.g. selected id "BDM1"
    // vs. preloaded "bdm1") would look deselected and get soft-deleted here.
    const selectedBdmIdSet = new Set(selectedBdmIds.map(normalizeUserId));
    const removedBdmIds = preloadedBdmIds.filter(
      (bdmUserId) => !selectedBdmIdSet.has(bdmUserId)
    );

    try {
      for (const bdmUserId of removedBdmIds) {
        // Sequential, not Promise.all: a failure partway through must stop
        // before the save runs, so a partial removal never gets papered over
        // by a "clean" success screen.
        // eslint-disable-next-line no-await-in-loop
        await removeHandler.mutateAsync({ salesAdminUserId, bdmUserId });
      }

      const response = await saveHandler.mutateAsync({
        salesAdminUserId: selectedSalesAdmin.userId,
        bdmUserIds: selectedBdmIds,
        allowAllClients,
      });
      setSubmitting(false);
      setResult({
        ...(response?.data ?? {}),
        removedCount: removedBdmIds.length,
      });
    } catch {
      setSubmitting(false);
    }
  };

  const handleDone = () => {
    onSubmit(result);
    onClose();
  };

  const handleRemoveAll = async () => {
    setShowRemoveAllConfirm(false);
    setRemovingAll(true);

    try {
      await removeAllHandler.mutateAsync({ salesAdminUserId });
      setRemovingAll(false);
      onSubmit();
      onClose();
    } catch {
      setRemovingAll(false);
    }
  };

  const salesAdminFilterChange = (event) => {
    setSalesAdminDataSource(filterBy(users.slice(), event.filter));
  };

  const bdmFilterChange = (event) => {
    setBdmDataSource(filterBy(users.slice(), event.filter));
  };

  const bdmItemRender = (li, itemProps) => {
    const itemChildren = (
      <span>
        <input
          type="checkbox"
          name={itemProps.dataItem}
          checked={itemProps.selected}
          onChange={(e) => itemProps.onClick(itemProps.index, e)}
        />
        &nbsp;{li.props.children}
      </span>
    );
    return React.cloneElement(li, li.props, itemChildren);
  };

  const displayName = selectedSalesAdmin?.userName ?? salesAdminUserId;

  const removeAllMessage = `This removes all ${activeMappingCount} active mapping(s) for ${displayName}. This Sales Admin will see no clients until mapped again. Continue?`;

  const dialogTitle = isEditing
    ? "Edit Sales Admin Mapping"
    : "Add Sales Admin Mapping";

  return (
    <Dialog title={dialogTitle} onClose={onClose} width={600}>
      <ErrorBanner handler={saveHandler} onError={() => setSubmitting(false)} />
      <ErrorBanner handler={removeHandler} onError={() => setSubmitting(false)} />
      <ErrorBanner
        handler={removeAllHandler}
        onError={() => setRemovingAll(false)}
      />

      {result ? (
        <div data-testid="save-outcomes">
          <p>
            {result.addedCount ?? 0} added, {result.reactivatedCount ?? 0}{" "}
            reactivated, {result.alreadyMappedCount ?? 0} already mapped,{" "}
            {result.invalidCount ?? 0} invalid, {result.mappedElsewhereCount ?? 0}{" "}
            mapped elsewhere.
          </p>
          {result.removedCount > 0 && (
            <p>
              {result.removedCount} deselected BDM(s) removed from this Sales
              Admin.
            </p>
          )}
          {(result.outcomes ?? [])
            .filter((outcome) => OUTCOMES_WITH_DETAIL.includes(outcome.outcome))
            .map((outcome, index) => (
              <p key={`${outcome.bdmUserId}-${index}`}>
                <b>{outcome.bdmUserId}</b> ({outcome.outcome}): {outcome.detail}
              </p>
            ))}
        </div>
      ) : (
        <>
          <p>
            Choose the Sales Admin and the BDMs whose clients they should see.
            Mapping a Sales Admin replaces whatever clients they would
            otherwise see, including their own assignments.
          </p>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontWeight: 500 }}>
              Sales Admin
            </label>
            <DropDownList
              data={salesAdminDataSource}
              textField={"userName"}
              dataItemKey={"userId"}
              value={selectedSalesAdmin}
              onChange={(e) => setSelectedSalesAdmin(e.target.value)}
              filterable={true}
              onFilterChange={salesAdminFilterChange}
              disabled={isEditing}
              style={{ width: "100%" }}
            />
            {!isEditing && (
              <PrismUserLookup
                label={"Add Sales Admin by user id"}
                onResolved={handleSalesAdminResolved}
              />
            )}
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontWeight: 500 }}>BDMs</label>
            <MultiSelect
              data={bdmDataSource}
              itemRender={bdmItemRender}
              textField={"userName"}
              dataItemKey={"userId"}
              autoClose={false}
              value={selectedBdms}
              onChange={(e) => setSelectedBdms(e.value)}
              filterable={true}
              onFilterChange={bdmFilterChange}
              disabled={allowAllClients}
              tags={
                selectedBdms.length > 0
                  ? [
                      {
                        text: `${selectedBdms.length} BDMs selected`,
                        data: [...selectedBdms],
                      },
                    ]
                  : []
              }
            />
            {!allowAllClients && (
              <PrismUserLookup
                label={"Add BDM by user id"}
                onResolved={handleBdmResolved}
              />
            )}
          </div>

          <div>
            <Checkbox
              label={"Allow all clients"}
              value={allowAllClients}
              onChange={(e) => setAllowAllClients(e.value)}
            />
            <p style={{ color: "#666", marginTop: 4 }}>
              This Sales Admin will see every client. Their mapped BDMs are
              ignored while this is on.
            </p>
          </div>
        </>
      )}

      <div style={{ paddingTop: 20 }}></div>
      <DialogActionsBar layout={"end"}>
        {result ? (
          <Button onClick={handleDone}>{"Close"}</Button>
        ) : (
          <>
            {isEditing && (
              <Button
                themeColor={"error"}
                fillMode={"flat"}
                disabled={removingAll}
                onClick={() => setShowRemoveAllConfirm(true)}
              >
                {"Remove All Mappings"}
              </Button>
            )}
            <Button onClick={onClose}>{"Cancel"}</Button>
            <Button
              type={"submit"}
              themeColor={"success"}
              disabled={!canSave}
              onClick={handleSubmit}
            >
              {"Save"}
            </Button>
          </>
        )}
      </DialogActionsBar>

      {showRemoveAllConfirm && (
        <ConfirmationDialog
          title={"Remove all mappings?"}
          message={removeAllMessage}
          onConfirm={handleRemoveAll}
          onCancel={() => setShowRemoveAllConfirm(false)}
        />
      )}
    </Dialog>
  );
};

export default SalesAdminMappingModal;
